import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Collapse,
  IconButton,
  Card,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import LockResetIcon from "@mui/icons-material/LockReset";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { motion } from "framer-motion";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data } = await axios.post(
        "https://le-travaille-server.onrender.com/auth/forgot-password",
        { email },
      );
      toast.success(data.message);
      sessionStorage.setItem("forgot-email", email);
      navigate("/auth/email-sent");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        p: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%", maxWidth: 450 }}
      >
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #003d38 0%, #00786F 100%)",
              color: "white",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "rgba(255,215,0,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                mb: 2,
              }}
            >
              <LockResetIcon sx={{ fontSize: 32, color: "#FFD700" }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Forgot Password?
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              Not a problem! We'll send you a link to reset your password.
            </Typography>
          </Box>

          <Box sx={{ p: 4 }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  label="Email Address"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  placeholder="Enter your registered email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#00786F" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,
                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },
                    mb: 2,
                  }}
                />

                {error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Collapse in={Boolean(error)}>
                      <Alert
                        severity="error"
                        action={
                          <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setError("")}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        }
                        sx={{ mb: 2, borderRadius: 2 }}
                      >
                        {error}
                      </Alert>
                    </Collapse>
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#00786F",
                      color: "#fff",
                      fontWeight: 600,
                      py: 1.8,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1.1rem",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "#005b54",
                        transform: "scale(1.02)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.3)",
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(0, 120, 111, 0.5)",
                      },
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </motion.div>

                <Box sx={{ mt: 3, textAlign: "center" }}>
                  <Button
                    onClick={() => navigate("/auth/login")}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      color: "#00786F",
                      fontWeight: 500,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(0, 120, 111, 0.04)",
                      },
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            </motion.div>
          </Box>
        </Card>
      </motion.div>
    </Box>
  );
};

export default ForgotPassword;
